import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RawTableIndexCell, RawTableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Pagination } from '@/components/ui/pagination';
import { useClientPagination } from '@/hooks/useClientPagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmActionDialog } from '@/components/ui/confirm-action-dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDeleteError } from '@/lib/api';

const NO_UNIT = '__no_unit__';

export default function ActivitiesListPage() {
  const [activities, setActivities] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ name: '', defaultUnitId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [toggling, setToggling] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ item: any; action: 'activate' | 'deactivate' } | null>(null);
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canDelete = hasPermission('activities:delete');
  const { page, setPage, pageSize, total, pageItems } = useClientPagination(activities, {
    resetKey: statusFilter,
  });

  useEffect(() => {
    loadUnits();
  }, []);

  useEffect(() => {
    loadActivities();
  }, [statusFilter]);

  async function loadUnits() {
    try { const res = await api.get<{ data: any[] }>('/units/all'); setUnits(res.data || []); } catch {}
  }

  async function loadActivities() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '1000');
      const res = await api.get<{ data: any[] }>(`/activities?${params.toString()}`);
      setActivities(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleSave() {
    if (!form.name.trim() || !form.defaultUnitId) return;
    setSaving(true);
    setError('');
    const defaultUnitId = form.defaultUnitId === NO_UNIT ? null : form.defaultUnitId;
    try {
      if (editing) {
        await api.put(`/activities/${editing.id}`, { name: form.name.trim(), defaultUnitId });
      } else {
        await api.post('/activities', { name: form.name.trim(), defaultUnitId });
      }
      setForm({ name: '', defaultUnitId: '' });
      setEditing(null);
      addToast(editing ? 'Updated' : 'Created', 'success');
      await loadActivities();
    } catch (e: any) { setError(e?.message || 'Failed'); }
    setSaving(false);
  }

  function handleCancel() {
    setForm({ name: '', defaultUnitId: '' });
    setEditing(null);
    setError('');
  }

  const handleDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/activities/${deleteTarget.id}`);
      addToast('Activity permanently deleted.', 'success');
      setDeleteTarget(null);
      await loadActivities();
    } catch (e: unknown) { addToast(formatDeleteError(e, 'activity'), 'error'); }
    setDeleting(false);
  }, [deleteTarget, deleting]);

  async function handleToggleStatus(item: any) {
    setToggling(item.id);
    try {
      await api.put(`/activities/${item.id}`, { isActive: !item.is_active });
      addToast(item.is_active ? `${item.name} deactivated successfully.` : `${item.name} activated successfully.`, 'success');
      await loadActivities();
    } catch (e: any) { addToast(e?.message || 'Failed', 'error'); }
    setToggling(null);
  }

  const formValid = form.name.trim() && form.defaultUnitId;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">Activities</h1>

      <Card>
        <CardHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch sm:items-end">
            <div className="space-y-1">
              <label className="text-sm font-medium">Activity Name</label>
              <Input placeholder="Activity name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Unit of Measurement</label>
              <Select value={form.defaultUnitId} onValueChange={v => setForm(f => ({ ...f, defaultUnitId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_UNIT}>Not Applicable</SelectItem>
                  {units.map((u: any) => <SelectItem key={u.id} value={u.id}>{u.symbol ? `${u.name} (${u.symbol})` : u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving || !formValid}>
                {editing ? 'Update' : <><Plus size={16} className="mr-1" />Add</>}
              </Button>
              {editing && (
                <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              )}
            </div>
          </div>
          {error && <p className="text-sm text-error mt-2">{error}</p>}
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              {(['all', 'active', 'inactive'] as const).map(v => (
                <Button key={v} type="button" size="sm" variant={statusFilter === v ? 'default' : 'outline'} onClick={() => setStatusFilter(v)}>
                  {v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive'}
                </Button>
              ))}
            </div>
          </div>
          {loading ? <p className="text-center py-8 text-secondary">Loading...</p> : activities.length === 0 ? (
            <p className="text-center py-8 text-secondary">No activities found</p>
          ) : (
            <>
            <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-0 text-sm">
              <thead><tr className="border-b"><RawTableIndexHead /><th className="text-left py-3">Name</th><th className="text-left py-3">Unit</th><th className="text-left py-3">Status</th><th className="text-right py-3">Actions</th></tr></thead>
              <tbody>{pageItems.map((a: any, index: number) => (
                <tr key={a.id} className="group border-b hover:bg-hover transition-colors">
                  <RawTableIndexCell value={rowSerial(index, page, pageSize)} />
                  <td className="py-3 font-medium">{a.name}</td>
                  <td className="py-3">{a.default_unit_name ? (a.default_unit_symbol ? `${a.default_unit_name} (${a.default_unit_symbol})` : a.default_unit_name) : <span className="text-secondary italic">Unit not assigned</span>}</td>
                  <td className="py-3"><Badge variant={a.is_active ? 'approved' : 'inactive'}>{a.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="py-3 text-right space-x-1">
                    {a.is_active ? (
                      <Button variant="ghost" size="sm" className="text-warning hover:text-warning" disabled={toggling === a.id} onClick={() => setConfirmTarget({ item: a, action: 'deactivate' })}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-success hover:text-success" disabled={toggling === a.id} onClick={() => setConfirmTarget({ item: a, action: 'activate' })}>
                        Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(a); setForm({ name: a.name, defaultUnitId: a.default_unit_id || NO_UNIT }); }} aria-label={`Edit ${a.name}`} title="Edit"><Pencil size={14} /></Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(a)} aria-label={`Permanently delete ${a.name}`} title="Permanently delete"><Trash2 size={14} /></Button>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
            </div>
            <div className="space-y-3 md:hidden">
              {pageItems.map((a: any) => (
                <div key={a.id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
                  <p className="font-medium text-primary break-words">{a.name}</p>
                  <dl className="text-sm space-y-1">
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Unit</dt><dd className="text-primary text-right break-words">{a.default_unit_name ? (a.default_unit_symbol ? `${a.default_unit_name} (${a.default_unit_symbol})` : a.default_unit_name) : <span className="text-secondary italic">Unit not assigned</span>}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-secondary shrink-0">Status</dt><dd><Badge variant={a.is_active ? 'approved' : 'inactive'}>{a.is_active ? 'Active' : 'Inactive'}</Badge></dd></div>
                  </dl>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {a.is_active ? (
                      <Button variant="ghost" size="sm" className="text-warning hover:text-warning min-h-[44px]" disabled={toggling === a.id} onClick={() => setConfirmTarget({ item: a, action: 'deactivate' })}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-success hover:text-success min-h-[44px]" disabled={toggling === a.id} onClick={() => setConfirmTarget({ item: a, action: 'activate' })}>
                        Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={() => { setEditing(a); setForm({ name: a.name, defaultUnitId: a.default_unit_id || NO_UNIT }); }} aria-label={`Edit ${a.name}`}>Edit</Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 min-h-[44px]" onClick={() => setDeleteTarget(a)} aria-label={`Permanently delete ${a.name}`}>Delete</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete Activity?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <strong>{deleteTarget?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete}>
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'deactivate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Deactivate ${confirmTarget?.item?.name}?`}
        description="This record will remain in existing records and reports, but it will not be available for new selections."
        actionLabel="Deactivate"
        buttonStyle="amber"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Deactivating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'activate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Activate ${confirmTarget?.item?.name}?`}
        description="This record will become available for new selections."
        actionLabel="Activate"
        buttonStyle="green"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Activating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />
    </div>
  );
}
